import { useState } from 'react';
import { Stack, RadioGroup, Radio, Heading, Icon, Text } from '@chakra-ui/react';
import { AiFillCheckCircle, AiFillCloseCircle } from 'react-icons/ai';
import { ItemModel } from 'models';
import { BasicItemCard } from 'components';
import Wizard from '../Wizard';

interface ItemRankChangeWizardProps {
  item: ItemModel;
  isDemoted: boolean;
  isLoading?: boolean;
  onSubmit?: (item: ItemModel, reason: string) => void;
  onRetry?: () => void;
  hasErrors?: boolean;
}

export default function ItemRankChangeWizard({
  item,
  isDemoted,
  isLoading = false,
  onSubmit = () => {},
  hasErrors = false
}: ItemRankChangeWizardProps) {
  const [selectedOption, setSelectedOption] = useState('');

  const action = isDemoted ? 'Promote' : 'Demote';

  const demoteReasons = [
    { value: 'UNOFFICIAL', label: 'This item is an unofficial release' },
    { value: 'DUPLICATE', label: 'This item is a duplicate of another item' },
    { value: 'INCORRECT_SALES', label: 'This item contains incorrect sales data' },
    { value: 'INCORRECT_DATA', label: 'This item contains incorrect data (non-sales)' },
    { value: 'OTHER', label: 'Other reason' }
  ];

  const promoteReasons = [
    { value: 'ISSUE_RESOLVED', label: 'The issue has been resolved' },
    { value: 'VERIFIED_AUTHENTIC', label: 'Item has been verified as authentic' },
    { value: 'DATA_CORRECTED', label: 'Item data has been corrected' },
    { value: 'OTHER', label: 'Other reason' }
  ];

  const reasons = isDemoted ? promoteReasons : demoteReasons;

  function renderRadioGroupSelection() {
    return (
      <Stack gap={4}>
        <Text fontSize="sm" color="gray.600">
          Please select a reason for {action.toLowerCase()}ing this item:
        </Text>
        <RadioGroup onChange={setSelectedOption} value={selectedOption} textAlign="left">
          <Stack direction="column" gap={2}>
            {reasons.map((reason) => (
              <Radio key={reason.value} size="sm" value={reason.value}>
                {reason.label}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </Stack>
    );
  }

  function renderRadioSelectionConfirmation() {
    const selectedReason = reasons.find(r => r.value === selectedOption);
    return (
      <Stack gap={4} alignItems="center">
        <Heading size="xs" textAlign="center">
          {selectedReason?.label}
        </Heading>
        <Text fontSize="sm" color="gray.600" textAlign="center">
          Are you sure you want to {action.toLowerCase()} this item?
        </Text>
      </Stack>
    );
  }

  function renderWizardFinishingSuccessStep() {
    if (hasErrors) {
      return (
        <Stack gap={4} alignItems="center">
          <Icon as={AiFillCloseCircle} color="red.500" w={10} h={10} />
          <Heading size="xs" textAlign="center">
            {action} Failed.<br />Please Try Again
          </Heading>
        </Stack>
      );
    } else {
      return (
        <Stack gap={4} alignItems="center">
          <Icon as={AiFillCheckCircle} color="green.500" w={10} h={10} />
          <Heading size="xs">
            Item {action}d Successfully
          </Heading>
        </Stack>
      );
    }
  }

  const onWizardSubmit = () => {
    onSubmit(item, selectedOption);
  };

  const wizardSteps = [
    {
      label: 'Reason',
      content: renderRadioGroupSelection(),
      requiresValidation: true,
      isValidated: selectedOption !== ''
    },
    {
      label: 'Confirm',
      content: renderRadioSelectionConfirmation()
    }
  ];

  return (
    <Stack gap={2} width="100%">
      <BasicItemCard item={item} />
      <Wizard
        steps={wizardSteps}
        finishStep={renderWizardFinishingSuccessStep()}
        onSubmit={onWizardSubmit}
        loading={isLoading}
      />
    </Stack>
  );
}
